// src/components/SignInButton.tsx
'use client';
import { useEffect, useState } from 'react';
import { getLocaleCookie } from '@/utils/localeCookiesManager.client';
import { fetchOAuthLoginUrl } from '@/features/auth/services/oauthApi';
import { useAuthStore } from '@/context/global/stores/authStore';
import IconArrowUp from '@/context/global/components/Icon/IconArrowUp';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { getMemberTierLabel } from '@/context/global/utils/getMemberTierLabel';
import { CWLogoutConfirm } from '@/context/global/components/g00/g00-d00-g012-cw-logout-confirm';

export default function CWSignInButtonMobile() {
  const t = useTranslations();
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [imgError, setImgError] = useState(false);

  const { token, user } = useAuthStore();

  // user is fetched by ClientInitializer at app level
  useEffect(() => {
    if (!token || user) {
      setChecked(true);
    }
  }, [token, user]);

  const handleLogin = async () => {
    setLoading(true);
    try {
      const lang = (getLocaleCookie() as 'th' | 'en') || 'th';
      const url = await fetchOAuthLoginUrl(lang);
      window.location.href = url;
    } catch (err) {
      alert(
        'Unable to start login: ' +
          (err instanceof Error ? err.message : 'Unknown error'),
      );
    } finally {
      setLoading(false);
    }
  };

  // Always render shimmer first
  if (!checked) {
    return (
      <div className="relative">
        <div className="h-10 w-10 animate-pulse rounded-full bg-gray-200" />
      </div>
    );
  }

  // If token exists, show avatar or shimmer
  if (token) {
    const fallbackAvatar = '/assets/ic-user.svg';
    const avatarUrl = user?.avatar;
    const avatarBg = null;
    const aliasName = user?.aliasName;
    const badge = user?.badge;
    const displayAvatar = avatarUrl && !imgError ? avatarUrl : fallbackAvatar;
    return (
      <div className="relative mt-3 pr-5">
        <div
          onClick={() => setShowDropdown((v) => !v)}
          className="flex w-full cursor-pointer items-center justify-between rounded-lg p-2 pr-[30px] text-black"
          style={
            avatarBg
              ? { background: avatarBg }
              : {
                  background:
                    'linear-gradient(315deg, #B2D233 14.8%, #A3C937 23.13%, #8CBC3F 37.96%, #8EBC43 46.29%, #94BE51 56.49%, #9FC169 67.6%, #AEC589 78.72%, #C1CAB3 89.84%, #CECECE 96.32%)',
                }
          }
        >
          <div className="flex items-center gap-5">
            <img
              src={displayAvatar}
              alt="User Avatar"
              className="flex size-10 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-gray-300"
              onError={() => setImgError(true)}
            />
            <div className="gap5 flex flex-col">
              <div className="mb-[-6] text-[24px] font-bold leading-tight text-[#030606]">
                {aliasName ?? '-'}
              </div>
              <div className="text-[20px] text-[#030606]">
                {getMemberTierLabel(badge)}
              </div>
            </div>
          </div>
          <IconArrowUp
            className={`transition-transform duration-200 ${showDropdown ? 'rotate-0' : 'rotate-180'}`}
          />
        </div>
        {showDropdown && (
          <div className="z-50 mt-2 flex w-full flex-col gap-2">
            <Link
              href="/edit-profile"
              className="w-full rounded-[8px] bg-[#0B0B0B] p-2 pl-5 text-left text-xl font-semibold transition hover:bg-[#1a1a1a]"
              onClick={() => setShowDropdown(false)}
            >
              {t('ProfileDropdown.editProfile')}
            </Link>
            <Link
              href="/favorites"
              className="w-full rounded-[8px] bg-[#0B0B0B] p-2 pl-5 text-left text-xl font-semibold transition hover:bg-[#1a1a1a]"
              onClick={() => setShowDropdown(false)}
            >
              {t('ProfileDropdown.favoriteSongs')}
            </Link>
            <Link
              href="/term"
              className="w-full rounded-[8px] bg-[#0B0B0B] p-2 pl-5 text-left text-xl font-semibold transition hover:bg-[#1a1a1a]"
              onClick={() => setShowDropdown(false)}
            >
              {t('ProfileDropdown.terms')}
            </Link>
            <Link
              href="/policy"
              className="w-full rounded-[8px] bg-[#0B0B0B] p-2 pl-5 text-left text-xl font-semibold transition hover:bg-[#1a1a1a]"
              onClick={() => setShowDropdown(false)}
            >
              {t('ProfileDropdown.policy')}
            </Link>
            <button
              onClick={() => {
                setShowDropdown(false);
                setShowLogoutConfirm(true);
              }}
              className="w-full rounded-[8px] bg-[#0B0B0B] p-2 pl-5 text-left text-xl font-semibold transition hover:bg-[#1a1a1a]"
            >
              {t('ProfileDropdown.logout')}
            </button>
          </div>
        )}
        <CWLogoutConfirm
          isOpen={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
        />
      </div>
    );
  }

  // If no token, show sign in button
  return (
    <button
      className="flex w-full items-center py-3 pr-12 text-left text-2xl text-white"
      onClick={handleLogin}
      disabled={loading}
      type="button"
    >
      <div className="relative">
        <p className="whitespace-pre leading-[normal]">
          {(() => {
            const locale = getLocaleCookie();
            if (locale === 'en') return 'Sign in';
            return 'เข้าสู่ระบบ';
          })()}
        </p>
      </div>
    </button>
  );
}
